import { useState, useEffect } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { 
  Settings, CreditCard, Users, FileText, Building2, 
  Plus, Edit, Trash2, Check, X, Loader2, Crown, Globe, Mail, Save
} from "lucide-react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import { SiteContentEditor } from "../components/SiteContentEditor";
import { SITE_SCHEMAS } from "../lib/siteSchema";

const SuperAdminPage = () => {
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("settings");
  
  // Settings
  const [settings, setSettings] = useState({
    bank_account: {
      bank_name: "",
      branch_name: "",
      account_type: "普通",
      account_number: "",
      account_holder: ""
    },
    payment_due_days: 14,
    reminder_days: [7, 3, 1],
    trial_days: 14,
    allow_yearly: true,
    yearly_discount_percent: 10
  });
  
  // Plans
  const [plans, setPlans] = useState([]);
  const [showPlanDialog, setShowPlanDialog] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [planForm, setPlanForm] = useState({
    name: "",
    price: 0,
    employee_limit: 5,
    features: [],
    sort_order: 0
  });
  
  // Invoices
  const [invoices, setInvoices] = useState([]);
  
  // Users
  const [users, setUsers] = useState([]);
  
  const [saving, setSaving] = useState(false);

  // Site content (CMS)
  const [sitePages, setSitePages] = useState([]);
  const [editingSlug, setEditingSlug] = useState(null);
  const [siteForm, setSiteForm] = useState({ title: "", nav_label: "", content: {}, show_in_nav: false, show_in_footer: false });
  const [contactMessages, setContactMessages] = useState([]);

  const featureOptions = [
    { key: "csv_export", label: "CSVエクスポート" },
    { key: "print", label: "印刷機能" },
    { key: "email", label: "メール通知" },
    { key: "api", label: "API連携" },
    { key: "multi_admin", label: "複数管理者" }
  ];

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };
      
      const [settingsRes, plansRes, invoicesRes, usersRes, sitePagesRes, contactRes] = await Promise.all([
        axios.get(`${API}/subscription/settings`, { headers }).catch(() => ({ data: settings })),
        axios.get(`${API}/subscription/plans/all`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/subscription/invoices/all`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/users`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/site/pages`).catch(() => ({ data: [] })),
        axios.get(`${API}/site/contact-messages`, { headers }).catch(() => ({ data: [] }))
      ]);
      
      if (settingsRes.data) setSettings(settingsRes.data);
      setPlans(plansRes.data || []);
      setInvoices(invoicesRes.data || []);
      setUsers(usersRes.data || []);
      setSitePages(sitePagesRes.data || []);
      setContactMessages(contactRes.data || []);
    } catch (error) {
      toast.error("データの取得に失敗しました");
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem("token");
      await axios.put(`${API}/subscription/settings`, settings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("設定を保存しました");
    } catch (error) {
      toast.error("設定の保存に失敗しました");
    } finally {
      setSaving(false);
    }
  };

  const selectSitePage = (page) => {
    setEditingSlug(page.slug);
    setSiteForm({
      title: page.title || "",
      nav_label: page.nav_label || "",
      content: page.content || {},
      show_in_nav: !!page.show_in_nav,
      show_in_footer: !!page.show_in_footer,
    });
  };

  const saveSitePage = async () => {
    if (!editingSlug) return;
    setSaving(true);
    try {
      const token = localStorage.getItem("token");
      await axios.put(`${API}/site/pages/${editingSlug}`, siteForm, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("ページを保存しました");
      const res = await axios.get(`${API}/site/pages`);
      setSitePages(res.data || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || "保存に失敗しました");
    } finally {
      setSaving(false);
    }
  };

  const openPlanDialog = (plan = null) => {
    if (plan) {
      setEditingPlan(plan);
      setPlanForm({
        name: plan.name,
        price: plan.price,
        employee_limit: plan.employee_limit,
        features: plan.features || [],
        sort_order: plan.sort_order || 0
      });
    } else {
      setEditingPlan(null);
      setPlanForm({
        name: "",
        price: 0,
        employee_limit: 5,
        features: [],
        sort_order: plans.length
      });
    }
    setShowPlanDialog(true);
  };

  const savePlan = async () => {
    if (!planForm.name) {
      toast.error("プラン名を入力してください");
      return;
    }
    
    setSaving(true);
    try {
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };
      
      if (editingPlan) {
        await axios.put(`${API}/subscription/plans/${editingPlan.id}`, planForm, { headers });
        toast.success("プランを更新しました");
      } else {
        await axios.post(`${API}/subscription/plans`, planForm, { headers });
        toast.success("プランを作成しました");
      }
      
      setShowPlanDialog(false);
      fetchData();
    } catch (error) {
      toast.error("プランの保存に失敗しました");
    } finally {
      setSaving(false);
    }
  };

  const deletePlan = async (planId) => {
    if (!window.confirm("このプランを削除しますか？")) return;
    
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/subscription/plans/${planId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("プランを削除しました");
      fetchData();
    } catch (error) {
      toast.error("プランの削除に失敗しました");
    }
  };

  const confirmPayment = async (invoiceId) => {
    if (!window.confirm("この支払いを確認済みにしますか？")) return;
    
    try {
      const token = localStorage.getItem("token");
      await axios.put(`${API}/subscription/invoices/${invoiceId}/confirm`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("支払いを確認しました");
      fetchData();
    } catch (error) {
      toast.error("支払い確認に失敗しました");
    }
  };

  const updateUserRole = async (userId, role) => {
    try {
      const token = localStorage.getItem("token");
      await axios.put(`${API}/admin/users/${userId}/role`, { role }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("ロールを更新しました");
      fetchData();
    } catch (error) {
      toast.error("ロールの更新に失敗しました");
    }
  };

  const toggleFeature = (feature) => {
    const features = planForm.features.includes(feature)
      ? planForm.features.filter(f => f !== feature)
      : [...planForm.features, feature];
    setPlanForm({ ...planForm, features });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Crown className="w-8 h-8 text-yellow-500" />
        <div>
          <h1 className="text-2xl font-bold">スーパー管理者</h1>
          <p className="text-slate-400 text-sm">サブスクリプション・ユーザー管理</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-slate-800">
          <TabsTrigger value="settings" className="data-[state=active]:bg-slate-700">
            <Building2 className="w-4 h-4 mr-2" />
            銀行口座設定
          </TabsTrigger>
          <TabsTrigger value="plans" className="data-[state=active]:bg-slate-700">
            <CreditCard className="w-4 h-4 mr-2" />
            プラン管理
          </TabsTrigger>
          <TabsTrigger value="invoices" className="data-[state=active]:bg-slate-700">
            <FileText className="w-4 h-4 mr-2" />
            請求書
          </TabsTrigger>
          <TabsTrigger value="users" className="data-[state=active]:bg-slate-700">
            <Users className="w-4 h-4 mr-2" />
            ユーザー
          </TabsTrigger>
          <TabsTrigger value="site" className="data-[state=active]:bg-slate-700">
            <Globe className="w-4 h-4 mr-2" />
            サイト内容
          </TabsTrigger>
        </TabsList>

        {/* Bank Account Settings */}
        <TabsContent value="settings" className="space-y-6">
          <Card className="bg-slate-800 border-slate-700">
            <CardHeader>
              <CardTitle>振込先銀行口座</CardTitle>
              <CardDescription>請求書に表示される銀行口座情報</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>銀行名</Label>
                  <Input
                    value={settings.bank_account?.bank_name || ""}
                    onChange={(e) => setSettings({
                      ...settings,
                      bank_account: { ...settings.bank_account, bank_name: e.target.value }
                    })}
                    placeholder="例: 三菱UFJ銀行"
                    className="bg-slate-700 border-slate-600"
                  />
                </div>
                <div className="space-y-2">
                  <Label>支店名</Label>
                  <Input
                    value={settings.bank_account?.branch_name || ""}
                    onChange={(e) => setSettings({
                      ...settings,
                      bank_account: { ...settings.bank_account, branch_name: e.target.value }
                    })}
                    placeholder="例: 渋谷支店"
                    className="bg-slate-700 border-slate-600"
                  />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>口座種別</Label>
                  <Select
                    value={settings.bank_account?.account_type || "普通"}
                    onValueChange={(v) => setSettings({
                      ...settings,
                      bank_account: { ...settings.bank_account, account_type: v }
                    })}
                  >
                    <SelectTrigger className="bg-slate-700 border-slate-600">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="普通">普通</SelectItem>
                      <SelectItem value="当座">当座</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>口座番号</Label>
                  <Input
                    value={settings.bank_account?.account_number || ""}
                    onChange={(e) => setSettings({
                      ...settings,
                      bank_account: { ...settings.bank_account, account_number: e.target.value }
                    })}
                    placeholder="1234567"
                    className="bg-slate-700 border-slate-600"
                  />
                </div>
                <div className="space-y-2">
                  <Label>口座名義</Label>
                  <Input
                    value={settings.bank_account?.account_holder || ""}
                    onChange={(e) => setSettings({
                      ...settings,
                      bank_account: { ...settings.bank_account, account_holder: e.target.value }
                    })}
                    placeholder="カ）ピーアールシー"
                    className="bg-slate-700 border-slate-600"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-slate-800 border-slate-700">
            <CardHeader>
              <CardTitle>支払い設定</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>支払い期限（日数）</Label>
                  <Input
                    type="number"
                    value={settings.payment_due_days || 14}
                    onChange={(e) => setSettings({ ...settings, payment_due_days: parseInt(e.target.value) })}
                    className="bg-slate-700 border-slate-600"
                  />
                </div>
                <div className="space-y-2">
                  <Label>無料トライアル（日数）</Label>
                  <Input
                    type="number"
                    value={settings.trial_days || 14}
                    onChange={(e) => setSettings({ ...settings, trial_days: parseInt(e.target.value) })}
                    className="bg-slate-700 border-slate-600"
                  />
                </div>
                <div className="space-y-2">
                  <Label>年払い割引（%）</Label>
                  <Input
                    type="number"
                    value={settings.yearly_discount_percent || 10}
                    onChange={(e) => setSettings({ ...settings, yearly_discount_percent: parseInt(e.target.value) })}
                    className="bg-slate-700 border-slate-600"
                  />
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Switch
                  checked={settings.allow_yearly || false}
                  onCheckedChange={(v) => setSettings({ ...settings, allow_yearly: v })}
                />
                <Label>年払いを許可する</Label>
              </div>
            </CardContent>
          </Card>

          <Button onClick={saveSettings} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
            設定を保存
          </Button>
        </TabsContent>

        {/* Plans Management */}
        <TabsContent value="plans" className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-semibold">サブスクリプションプラン</h2>
            <Button onClick={() => openPlanDialog()} className="bg-emerald-600 hover:bg-emerald-700">
              <Plus className="w-4 h-4 mr-2" />
              新規プラン
            </Button>
          </div>

          <div className="grid gap-4">
            {plans.map((plan) => (
              <Card key={plan.id} className={`bg-slate-800 border-slate-700 ${!plan.is_active ? 'opacity-50' : ''}`}>
                <CardContent className="p-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-lg">{plan.name}</h3>
                        {!plan.is_active && <Badge variant="secondary">無効</Badge>}
                      </div>
                      <p className="text-2xl font-bold text-emerald-400 mt-1">
                        ¥{plan.price.toLocaleString()}<span className="text-sm text-slate-400">/月</span>
                      </p>
                      <p className="text-slate-400 text-sm mt-1">従業員数: {plan.employee_limit}名まで</p>
                      <div className="flex flex-wrap gap-2 mt-2">
                        {plan.features?.map((f) => (
                          <Badge key={f} variant="outline" className="text-xs">
                            {featureOptions.find(o => o.key === f)?.label || f}
                          </Badge>
                        ))}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={() => openPlanDialog(plan)}>
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => deletePlan(plan.id)} className="text-red-400 hover:text-red-300">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
            {plans.length === 0 && (
              <div className="text-center text-slate-400 py-8">
                プランがありません。新規プランを作成してください。
              </div>
            )}
          </div>
        </TabsContent>

        {/* Invoices */}
        <TabsContent value="invoices" className="space-y-4">
          <h2 className="text-lg font-semibold">請求書一覧</h2>
          <div className="space-y-3">
            {invoices.map((invoice) => (
              <Card key={invoice.id} className="bg-slate-800 border-slate-700">
                <CardContent className="p-4">
                  <div className="flex justify-between items-center">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-sm">{invoice.invoice_number}</span>
                        <Badge variant={
                          invoice.status === "paid" ? "default" :
                          invoice.status === "pending" ? "secondary" : "destructive"
                        }>
                          {invoice.status === "paid" ? "支払済" :
                           invoice.status === "pending" ? "未払い" : "期限切れ"}
                        </Badge>
                      </div>
                      <p className="text-slate-400 text-sm mt-1">
                        {invoice.user?.email} - {invoice.plan_name}
                      </p>
                      <p className="text-lg font-semibold mt-1">¥{invoice.amount.toLocaleString()}</p>
                      <p className="text-xs text-slate-500">
                        期限: {new Date(invoice.due_date).toLocaleDateString('ja-JP')}
                      </p>
                    </div>
                    {invoice.status === "pending" && (
                      <Button onClick={() => confirmPayment(invoice.id)} className="bg-emerald-600 hover:bg-emerald-700">
                        <Check className="w-4 h-4 mr-2" />
                        支払確認
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
            {invoices.length === 0 && (
              <div className="text-center text-slate-400 py-8">
                請求書がありません
              </div>
            )}
          </div>
        </TabsContent>

        {/* Users */}
        <TabsContent value="users" className="space-y-4">
          <h2 className="text-lg font-semibold">ユーザー管理</h2>
          <div className="space-y-3">
            {users.map((user) => (
              <Card key={user.id} className="bg-slate-800 border-slate-700">
                <CardContent className="p-4">
                  <div className="flex justify-between items-center">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{user.name}</span>
                        <Badge variant={user.role === "super_admin" ? "default" : "secondary"}>
                          {user.role === "super_admin" ? "スーパー管理者" : "管理者"}
                        </Badge>
                      </div>
                      <p className="text-slate-400 text-sm">{user.email}</p>
                      {user.subscription && (
                        <p className="text-xs text-emerald-400 mt-1">
                          {user.subscription.plan?.name} - {user.subscription.status}
                        </p>
                      )}
                    </div>
                    <Select
                      value={user.role}
                      onValueChange={(v) => updateUserRole(user.id, v)}
                    >
                      <SelectTrigger className="w-40 bg-slate-700 border-slate-600">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="admin">管理者</SelectItem>
                        <SelectItem value="super_admin">スーパー管理者</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Site Content (CMS) */}
        <TabsContent value="site" className="space-y-4">
          <div className="grid lg:grid-cols-[260px_1fr] gap-4">
            <Card className="bg-slate-800 border-slate-700 self-start">
              <CardContent className="p-3">
                <p className="text-xs text-slate-400 px-2 py-1 font-semibold">ページ一覧</p>
                <div className="space-y-1">
                  {sitePages.map((p) => (
                    <button
                      key={p.slug}
                      onClick={() => selectSitePage(p)}
                      data-testid={`site-page-item-${p.slug}`}
                      className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${editingSlug === p.slug ? "bg-emerald-600/20 text-emerald-400" : "text-slate-300 hover:bg-slate-700"}`}
                    >
                      {p.nav_label}
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card className="bg-slate-800 border-slate-700">
              <CardContent className="p-5">
                {!editingSlug ? (
                  <p className="text-slate-400 text-sm py-8 text-center">左の一覧から編集するページを選択してください。</p>
                ) : (
                  <div className="space-y-4" data-testid="site-editor">
                    <div className="space-y-2">
                      <Label>ページタイトル</Label>
                      <Input value={siteForm.title} onChange={(e) => setSiteForm({ ...siteForm, title: e.target.value })} className="bg-slate-700 border-slate-600" data-testid="site-title-input" />
                    </div>
                    <div className="space-y-2">
                      <Label>メニュー表示名</Label>
                      <Input value={siteForm.nav_label} onChange={(e) => setSiteForm({ ...siteForm, nav_label: e.target.value })} className="bg-slate-700 border-slate-600" data-testid="site-navlabel-input" />
                    </div>
                    <div className="space-y-2">
                      <Label>ページ内容</Label>
                      <SiteContentEditor
                        schema={SITE_SCHEMAS[editingSlug]}
                        content={siteForm.content}
                        onChange={(content) => setSiteForm({ ...siteForm, content })}
                      />
                    </div>
                    <div className="flex flex-wrap items-center gap-6">
                      <div className="flex items-center gap-2">
                        <Switch checked={siteForm.show_in_nav} onCheckedChange={(v) => setSiteForm({ ...siteForm, show_in_nav: v })} data-testid="site-shownav-switch" />
                        <span className="text-sm">ヘッダーメニューに表示</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Switch checked={siteForm.show_in_footer} onCheckedChange={(v) => setSiteForm({ ...siteForm, show_in_footer: v })} data-testid="site-showfooter-switch" />
                        <span className="text-sm">フッターに表示</span>
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <Button onClick={saveSitePage} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="site-save-btn">
                        {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}保存
                      </Button>
                      <a href={`/p/${editingSlug}`} target="_blank" rel="noreferrer">
                        <Button variant="outline" className="border-slate-600">プレビュー</Button>
                      </a>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          <Card className="bg-slate-800 border-slate-700">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Mail className="w-4 h-4" />
                お問い合わせ受信一覧（{contactMessages.length}）
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {contactMessages.length === 0 ? (
                <p className="text-slate-400 text-sm">まだお問い合わせはありません。</p>
              ) : (
                contactMessages.map((m) => (
                  <div key={m.id} className="p-3 rounded-lg bg-slate-700/50 border border-slate-700" data-testid={`contact-msg-${m.id}`}>
                    <div className="flex justify-between items-start gap-4">
                      <div>
                        <p className="font-medium">
                          {m.name} {m.company && <span className="text-slate-400 text-sm">/ {m.company}</span>}
                        </p>
                        <p className="text-xs text-slate-400">{m.email}{m.phone ? ` / ${m.phone}` : ""}</p>
                      </div>
                      <span className="text-xs text-slate-500">{(m.created_at || "").slice(0, 10)}</span>
                    </div>
                    <p className="text-sm text-slate-300 mt-2 whitespace-pre-wrap">{m.message}</p>
                  </div>
                ))
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Plan Dialog */}
      <Dialog open={showPlanDialog} onOpenChange={setShowPlanDialog}>
        <DialogContent className="max-w-md bg-slate-800 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle>{editingPlan ? "プラン編集" : "新規プラン作成"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>プラン名</Label>
              <Input
                value={planForm.name}
                onChange={(e) => setPlanForm({ ...planForm, name: e.target.value })}
                placeholder="例: スタンダード"
                className="bg-slate-700 border-slate-600"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>月額料金（円）</Label>
                <Input
                  type="number"
                  value={planForm.price}
                  onChange={(e) => setPlanForm({ ...planForm, price: parseInt(e.target.value) || 0 })}
                  className="bg-slate-700 border-slate-600"
                />
              </div>
              <div className="space-y-2">
                <Label>従業員上限</Label>
                <Input
                  type="number"
                  value={planForm.employee_limit}
                  onChange={(e) => setPlanForm({ ...planForm, employee_limit: parseInt(e.target.value) || 5 })}
                  className="bg-slate-700 border-slate-600"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>機能</Label>
              <div className="space-y-2">
                {featureOptions.map((feature) => (
                  <div key={feature.key} className="flex items-center gap-2">
                    <Switch
                      checked={planForm.features.includes(feature.key)}
                      onCheckedChange={() => toggleFeature(feature.key)}
                    />
                    <span className="text-sm">{feature.label}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex gap-3 pt-2">
              <Button variant="outline" onClick={() => setShowPlanDialog(false)} className="flex-1 border-slate-600">
                キャンセル
              </Button>
              <Button onClick={savePlan} disabled={saving} className="flex-1 bg-emerald-600 hover:bg-emerald-700">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "保存"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SuperAdminPage;
